import { isRetailerCreatedOver30DaysOld } from "@faire/retailer-visitor-shared/lib/isRetailerCreatedOver30DaysOld";
import { flashSaleSeasonalWarehouseSaleIsOn } from "@faire/retailer-visitor-shared/settings/getSettingSeasonalWarehouseSale";
import { useSettingMigrateRetailerStore } from "@faire/retailer-visitor-shared/settings/useSettingMigrateRetailerStore";
import { useRetailerStore } from "@faire/retailer-visitor-shared/stores/domain/RetailerStore";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web--source/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { ISO3166Alpha3 } from "@faire/web-api--source/indigofair/iso3166/ISO3166Alpha3";
import { create } from "zustand";

interface RetailerFlashSale {
  showFlashSales: boolean;
  showFlashSalesAsUnavailable: boolean;
  showWarehouseFlashSale: boolean;
  setRetailerFlashSale: (
    retailer: Partial<IRetailer>,
    userCanPlaceOrder: boolean
  ) => void;
}

export const useZustandRetailerFlashSale = create<RetailerFlashSale>((set) => ({
  showFlashSales: false,
  showFlashSalesAsUnavailable: false,
  showWarehouseFlashSale: false,
  setRetailerFlashSale: (retailer, userCanPlaceOrder) => {
    const retailerCountry = retailer.country as ISO3166Alpha3;
    const isAmerican =
      retailerCountry === THREE_LETTER_PRIMARY_COUNTRY_CODES.USA;

    const showFlashSales =
      userCanPlaceOrder &&
      !!retailer &&
      !retailer.hide_redistribution_channels &&
      (isAmerican || retailerCountry === undefined) &&
      isRetailerCreatedOver30DaysOld(retailer.created_at);

    const showFlashSalesAsUnavailable =
      userCanPlaceOrder &&
      !!retailer.hide_redistribution_channels &&
      !!retailer.show_flash_sales_mix_and_match_as_unavailable &&
      (isAmerican || retailerCountry === undefined);

    const showWarehouseFlashSale =
      showFlashSales && flashSaleSeasonalWarehouseSaleIsOn();

    set({
      showFlashSales,
      showFlashSalesAsUnavailable,
      showWarehouseFlashSale,
    });
  },
}));

const useFlashSaleControl = () => {
  const {
    showFlashSales: showFlashSalesControl,
    showFlashSalesAsUnavailable: showFlashSalesAsUnavailableControl,
    showWarehouseFlashSale: showWarehouseFlashSaleControl,
  } = useRetailerStore([
    "showFlashSales",
    "showFlashSalesAsUnavailable",
    "showWarehouseFlashSale",
  ]);

  return {
    showFlashSales: showFlashSalesControl,
    showFlashSalesAsUnavailable: showFlashSalesAsUnavailableControl,
    showWarehouseFlashSale: showWarehouseFlashSaleControl,
  };
};

export const useFlashSale = () => {
  const shouldUseHooksMigration = useSettingMigrateRetailerStore();
  if (shouldUseHooksMigration) {
    // eslint-disable-next-line react-compiler/react-compiler, react-hooks/rules-of-hooks
    return useZustandRetailerFlashSale();
  }
  // eslint-disable-next-line react-compiler/react-compiler, react-hooks/rules-of-hooks
  return useFlashSaleControl();
};
